import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AddCSSThemePayload, AddLayoutPayload, AddPagePayload, CopyCSSThemePayload, CopyCSSThemeResponse, CopyDashboardPayload, CopyDashboardResponse, CopyLayoutPayload, CopyLayoutResponse, CopyPagePayload, CopyPageResponse } from '../_models/page.model';
import {
  ChangeUserPasswordPayload,
  CopyAppPayload,
  CopyRolePayload,
  CopyRoleResponse,
  ImpersonateUserPayload,
  SendMFACodeMailPayload,
  SendPasswordRecoveryMailPayload,
} from '../_models/settings.models';
import { PasswordRecovery } from '../_models/auth.models';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private baseURL = `${environment.apiUrl}/api`;
  private supersetLoginURL = environment.supersetUrl
  private midURL = '/user';
  private APPLICATION_USERS_URL = '/getapplicationusers?applicationId=';
  private COPY = '/copy';


  public get getApplicationUserUrl() {
    return `${this.baseURL}${this.midURL}${this.APPLICATION_USERS_URL}`;
  }

  public get getCopyDashboardUrl() {
    return `${this.baseURL}/dashboard${this.COPY}`;
  }

  public get getCopyRoleUrl() {
    return `${this.baseURL}/roles${this.COPY}`;
  }

  public get getCopyPageUrl() {
    return `${this.baseURL}/pages${this.COPY}`;
  }

  public get getCopyCSSThemeUrl() {
    return `${this.baseURL}/customcss${this.COPY}`;
  }

  public get getCopyLayoutUrl() {
    return `${this.baseURL}/layout${this.COPY}`;
  }

  constructor(private http: HttpClient) {}

  /**
   * Get Application Users
   */
  getApplicationUsers = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.getApplicationUserUrl}${applicationId}`,
      {}
    );

  /**
   * Get Roles
   */

  getRoles = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/user/getapplicationroles?applicationId=${applicationId}`,
      {}
    );

  /**
   * Get Pages
   */
  getPages = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/pages/getpagebyapplicationid?id=${applicationId}`,
      {}
    );

  /**
   * Get default page for table for impersonate
   */
  getDefaultPageByTableId = (tableId: string): Observable<any> => {
    return this.http.get(
      `${this.baseURL}/applicationtable/defaultpagebytableid?tableId=${tableId}`
    );
  };

  /**
   * Get Dashboards
   */
  getDashboards = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/dashboards?id=${applicationId}`,
      {}
    );
  /**
   * Get Dashboard y Id 
   */
  getDashboardById = (dashboardId: string): Observable<any> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/dashboard?id=${dashboardId}`,
      {}
    );

  /**
   * Get Products
   */
  getProducts = (): Observable<Array<any>> =>
    this.http.get<Array<any>>(`${this.baseURL}/stripe/products`, {});

  /**
   * Get Company Users
   */
  getCompanyUsers = (
    companyId: string,
    applicationId: any,
    isSearch: boolean,
    search: string,
    isScroll: boolean,
    skip: number,
    pageSize: number,
    isOptimizedComponent: boolean
  ): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/user/getcompanyusers?companyId=${companyId}&applicationId=${applicationId}&isSearch=${isSearch}&search=${search}&isScroll=${isScroll}&skip=${skip}&pageSize=${pageSize}&isOptimizedComponent=${isOptimizedComponent}`,
      {}
    );
  
  getUsersByEntityId = (
    entityId: string,
    query: string,
    skip: number,
    pageSize: number
  ): Observable<{ users: any[]; hasMore: boolean }> =>
    this.http.get<{ users: any[]; hasMore: boolean }>(
      `${this.baseURL}/user?entityId=${entityId}&query=${encodeURIComponent(query)}&skip=${skip}&pageSize=${pageSize}`,
      {}
    );

  /**
   * Add Existing user to the application
   */
  addExistingUserToApplication = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/addtoapp`, payload, {});

  /**
   * Delete An Existing User
   */

  deleteAnApplicationUser = (appUserId: any, appId: any): Observable<any> =>
    this.http.delete<any>(
      `${this.baseURL}/user?id=${appUserId}&appId=${appId}`,
      {}
    );

  /**
   * Update an Application user
   */

  updateApplicationUser = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/updaterole`, payload, {});

  /**
   * Invite New User
   */
  inviteNewUser = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/inviteuser`, payload, {});

  /**
   *
   * Resend Invite
   */
  resendInvite = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/resendinvite`, payload, {});

  /**
   *
   * Delete Invite
   */
  deleteInvite = (Id: string): Observable<any> =>
  this.http.delete<any>(
    `${this.baseURL}/user/invite?id=${Id}`,
    {}
  );

  /**
   * Send Password Recovery Main
   */
  sendPasswordRecoveryMail = (
    payload: SendPasswordRecoveryMailPayload
  ): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/forgotpassword`, payload, {});

  /**
   * Send Password Recovery Main From Admin/Super Admin
   */
  sendRecoveryMailFromAdmin = (
    payload: SendPasswordRecoveryMailPayload
  ): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/forgotpasswordbyadmin`, payload, {});

  /**
   * Send Password Recovery Main
   */
  sendMFACodeMail = (
    payload: SendMFACodeMailPayload
  ): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/resendmfacode`, payload, {});

  /**
   * Change User Password
   */
  recoverUserPassword = (
    payload: PasswordRecovery
  ): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/recoverpassword`, payload, {});

  /**
   * Change User Password
   */
  changeUserPassword = (
    payload: ChangeUserPasswordPayload
  ): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/resetpassword`, payload, {});

  /**
   * Impersonate a User
   */
  impersonateUser = (payload: ImpersonateUserPayload): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/user/impersonateUser`, payload, {});

  /**
   * Delete Impersonation
   */
  deleteImpersonation = (impersonateId: string): Observable<any> =>
    this.http.delete<any>(
      `${this.baseURL}/user/impersonateuser?id=${impersonateId}`,
      {}
    );

  /**
   * Get Management Types
   */

  getManagementTypes = (): Observable<any> =>
    this.http.get<Array<any>>(`${this.baseURL}/roles/managementtypes`, {});

  /**
   * Create Role
   */
  createRole = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/roles`, payload, {});

  /**
   * Delete Role
   */
  deleteRole = (roleId: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/roles?Id=${roleId}`, {});

  /**
   * Create Role
   */
  updateRole = (payload: any): Observable<any> =>
    this.http.put<any>(`${this.baseURL}/roles`, payload, {});

  /**
   * Copy Report
   */
 copyRole = (payload: CopyRolePayload): Observable<CopyRoleResponse> =>
 this.http.post<CopyRoleResponse>(`${this.getCopyRoleUrl}`, payload);

  /**
   * Get Role Detail by Id
   */
  getRoleById = (roleId: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/roles?Id=${roleId}`, {});

  /**
   *
   * @returns Page Detail by page Id
   */
  getPage = (pageId: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/pages?id=${pageId}`, {});

  /**
   * Add a Page
   */

  addPage = (payload: AddPagePayload): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/pages`, payload, {});

  /**
   *
   * Updatea a Page
   */

  updatePage = (payload: AddPagePayload): Observable<any> =>
    this.http.put<any>(`${this.baseURL}/pages`, payload, {});

  /**
   * Delete a Page
   */

  deletePage = (pageId: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/pages?id=${pageId}`, {});

  /**
   * Get Summary Fields
   */

  getSummaryFields = (appId: string): Observable<any> =>
    this.http.get<any>(
      `${this.baseURL}/dashboard/tablesummaries?id=${appId}`,
      {}
    );

  /**
   * Get Reports
   */
  getReports = (appId: string, reportType: string): Observable<any> =>
    this.http.get<any>(
      `${this.baseURL}/report/getall?id=${appId}&reportType=${reportType}`,
      {}
    );

  /**
   * Get Summary Field Value
   */
  getSummaryFieldValue = (summaryId: number): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/dashboard/summary?id=${summaryId}`, {});

  /**
   * Save a Dashboard
   */
  saveDashboard = (payload: any): Observable<any> =>
    this.http.post(`${this.baseURL}/dashboard`, payload, {});

  /**
   *
   * Update a Dashboard
   */
  updateDashboard = (payload: any): Observable<any> =>
    this.http.put(`${this.baseURL}/dashboard`, payload, {});

  /**
   * Delete a Dashboard
   */
  deleteDashboard = (dashboardId: string): Observable<any> =>
    this.http.delete(`${this.baseURL}/dashboard?id=${dashboardId}`, {});

  /**
   * Get Copy Apps
   */
  getCopyApps = (appId: string): Observable<any> =>
    this.http.get(
      `${this.baseURL}/application/copiedapps?applicationId=${appId}`
    );

  /**
   * Create Copy Apps
   */
  createCopyApps = (payload: CopyAppPayload): Observable<any> =>
    this.http.post(`${this.baseURL}/application/createlike`, payload);

  /**
   * Delete Copy Apps
   */
  deleteCopyApp = (appId: any): Observable<any> =>
    this.http.delete(`${this.baseURL}/application/removecopiedapp?id=${appId}`);

  /**
   * Create New User
   */
  createNewUser = (payload: any): Observable<any> =>
    this.http.post(`${this.baseURL}/user/create`, payload);

  /**
   * Valid User Email
   */
  validUserEmail = (payload: any): Observable<any> =>
    this.http.post(`${this.baseURL}/user/validemail`, payload);

  /**
   * Copy Dashboard
   */
   copyDashboard = (payload: CopyDashboardPayload): Observable<CopyDashboardResponse> =>
   this.http.post<CopyDashboardResponse>(`${this.getCopyDashboardUrl}`, payload);

   /**
   * Copy Page
   */
    copyPage = (payload: CopyPagePayload): Observable<CopyPageResponse> =>
    this.http.post<CopyPageResponse>(`${this.getCopyPageUrl}`, payload);

  /**
   * Get Themes
   */
  getCssThemes = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/customcss/getbyapplicationid?id=${applicationId}`,
      {}
    );

  /**
   *
   * @returns Theme Detail by page Id
   */
  getCssTheme = (themeId: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/customcss?id=${themeId}`, {});

  /**
   * Add a Theme
   */

  addCssTheme = (payload: AddCSSThemePayload): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/customcss`, payload, {});

  /**
   *
   * Updatea a Theme
   */

  updateCssTheme = (payload: AddCSSThemePayload): Observable<any> =>
    this.http.put<any>(`${this.baseURL}/customcss`, payload, {});

  /**
   * Delete a Theme
   */

  deleteCssTheme = (themeId: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/customcss?id=${themeId}`, {});


  getDefaultCSSByApplicationId = (applicationId: string): Observable<any> => {
    return this.http.get(
      `${this.baseURL}/customcss/getappdefaultcss?applicationId=${applicationId}`
    );
  };

   /**
   * Copy Theme
   */
    copyCssTheme = (payload: CopyCSSThemePayload): Observable<CopyCSSThemeResponse> =>
    this.http.post<CopyCSSThemeResponse>(`${this.getCopyCSSThemeUrl}`, payload);

    /**
   * Get Themes
   */
    getAllLayouts = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/layout/getbyapplicationid?id=${applicationId}`,
      {}
    );

  /**
   *
   * @returns Theme Detail by page Id
   */
  getLayout = (layoutId: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/layout?id=${layoutId}`, {});

  /**
   * Add a Theme
   */

  addLayout = (payload: AddLayoutPayload): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/layout`, payload, {});

  /**
   *
   * Updatea a Theme
   */

  updateLayout = (payload: AddLayoutPayload): Observable<any> =>
    this.http.put<any>(`${this.baseURL}/layout`, payload, {});

  /**
   * Delete a Theme
   */

  deleteLayout = (layoutId: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/layout?id=${layoutId}`, {});


  getDefaultLayoutByApplicationId = (applicationId: string): Observable<any> => {
    return this.http.get(
      `${this.baseURL}/layout/getappdefaultcss?applicationId=${applicationId}`
    );
  };

   /**
   * Copy Theme
   */
    copyLayout = (payload: CopyLayoutPayload): Observable<CopyLayoutResponse> =>
    this.http.post<CopyLayoutResponse>(`${this.getCopyLayoutUrl}`, payload);

   /**
   * Get all superset dashboard
   */
    getAllSupersetDashboards = (applicationId: string): Observable<Array<any>> =>
      this.http.get<Array<any>>(
        `${this.baseURL}/supersetdashboards?id=${applicationId}`,
        {}
      );

    /**
     *
     * @returns get superset dashboard detail
     */
    getSupersetDashboard = (layoutId: string): Observable<any> =>
      this.http.get<any>(`${this.baseURL}/supersetdashboard?id=${layoutId}`, {});

   /**
   * Delete superset dashboard from superset db
   */

  deleteDashboardFromSuperset = (auth_token:string | null, dashboardId: string): Observable<any> =>
    this.http.get<any>(`${this.supersetLoginURL}/login?token=${auth_token}&dashboardid=${dashboardId}&next=/delete/`, {});

  /**
   * Delete a Superset Dashboard
   */

  deleteSupersetDashboard = (dashboardId: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/supersetdashboard?id=${dashboardId}`, {});

  /**
   * Resend or Forward Email log
   */

  resendForwardNotificationLog = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/application/resendforwardemail`, payload, {});

   /**
   *
   * @returns Notification Log Detail by Id
   */
   getNotificationLogDetail = (appId:string,tableId:string,id: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/application/getnotificationlogdata?recordId=${id}&tableId=${tableId}`, {});

  getAllMetabaseDashboards = (applicationId: string): Observable<Array<any>> =>
    this.http.get<Array<any>>(
      `${this.baseURL}/metabasedashboards?id=${applicationId}`,
      {}
    );

  getMetabaseDashboard = (id: string): Observable<any> =>
    this.http.get<any>(`${this.baseURL}/MetaBaseDashboard?id=${id}`, {});

  addMetabaseDashboard = (payload: any): Observable<any> =>
    this.http.post<any>(`${this.baseURL}/MetaBaseDashboard`, payload, {});

  updateMetabaseDashboard = (payload: any): Observable<any> =>
    this.http.put<any>(`${this.baseURL}/MetaBaseDashboard`, payload, {});

  deleteMetabaseDashboard = (id: string): Observable<any> =>
    this.http.delete<any>(`${this.baseURL}/MetaBaseDashboard?id=${id}`, {});
}
